// FX — per-tier cross-currency card-spend revenue and subsidy cost.
//
// For each tier, spending in a non-home currency triggers Wirex's FX
// passthrough (wirex.fxPassThrough, currently 0.5% in the model). Pulsar
// either absorbs it (under cap) or passes it through with a margin (above
// cap). Each tier's cap and above-cap rate define the design.
//
// Returns per-active-user EUR / month figures: revenue and subsidy cost.

import { blendedFxUsageRate, blendedFxAvgVolume } from './regions.js';

/**
 * FX line for a single tier in a single month, per active user.
 *
 * Subsidy adoption distributions integrate over the user base in forecast.js;
 * here we use the median (p50) as the central estimate for FX-cap-hitting,
 * and the regional FX-using rate for "any non-home spend."
 *
 * @param {object} inputs
 * @param {object} tier        single tier from inputs.tiers[k]
 * @param {object} scenario    inputs.scenarios[k]
 * @param {number} month
 * @returns {{ rev: number, cost: number, fxVol: number }}
 *   rev:  EUR / active user / month — Pulsar's FX margin above cap
 *   cost: EUR / active user / month — Wirex passthrough Pulsar absorbs under cap
 *   fxVol: EUR / active user / month — total non-home volume for diagnostics
 */
export function fxPerUser(inputs, tier, scenario, month) {
  const fxUsage = blendedFxUsageRate(inputs, month);
  const fxAvg = blendedFxAvgVolume(inputs, month);
  const expectedVol = fxUsage * fxAvg; // EUR / active user / month average
  // Cap is in USD; convert to EUR for the math
  const capEur = tier.fxCap / inputs.fx.eurToUsd;

  const underCap = Math.min(expectedVol, capEur);
  const aboveCap = Math.max(0, expectedVol - capEur);

  // Below cap: Pulsar absorbs Wirex passthrough cost
  const cost = underCap * inputs.wirex.fxPassThrough;
  // Above cap: user pays tier.fxAboveRate; Pulsar still pays Wirex passthrough
  const userPays = aboveCap * tier.fxAboveRate;
  const wirexCostAbove = aboveCap * inputs.wirex.fxPassThrough;
  const rev = userPays - wirexCostAbove;

  return { rev: Math.max(0, rev), cost, fxVol: expectedVol };
}
